import {OktaAuth} from '@okta/okta-auth-js';
import {interval, merge, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

/**
 * Outils de synchronisation multi-onglets.
 *
 * Chaque onglet exécute le même code de push/pull : il n'y a pas d'onglet « leader ».
 * Les opérations sont sérialisées entre onglets par des Web Locks pris le temps d'une opération
 * (un onglet gelé en arrière-plan ne bloque donc jamais les autres durablement).
 */

export const PUSH_FALLBACK_INTERVAL_MS = 60_000;
export const PULL_MIN_INTERVAL_MS = 55_000;

export interface TabLockOptions {
    ifAvailable?: boolean;
}

/**
 * Exécute fn sous un Web Lock nommé partagé entre tous les onglets de l'origine.
 * Avec ifAvailable, renvoie null sans exécuter fn si le lock est déjà pris par un autre onglet.
 */
export async function withTabLock<T>(name: string, fn: () => Promise<T>, options: TabLockOptions = {}): Promise<T | null> {
    const locks: LockManager | undefined = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks) {
        return fn();
    }
    return locks.request(name, {ifAvailable: !!options.ifAvailable}, async lock => {
        if (!lock) {
            return null;
        }
        return fn();
    });
}

/**
 * Déclenche run() à chaque changement des documents en attente (query réactive RxDB),
 * ainsi que périodiquement en secours (erreur réseau, token expiré...).
 * Les déclenchements pendant une exécution en cours dans le même onglet sont ignorés.
 */
export function watchPending(collection: any, selector: any, run: () => Promise<any>,
                             fallbackIntervalMs = PUSH_FALLBACK_INTERVAL_MS): Subscription {
    let running = false;
    const trigger = async () => {
        if (running) {
            return;
        }
        running = true;
        try {
            await run();
        } catch (err) {
            console.error('[MultiTabSync] Error while processing pending documents:', err);
        } finally {
            running = false;
        }
    };
    return merge(
        collection.find({selector}).$.pipe(debounceTime(500)),
        interval(fallbackIntervalMs)
    ).subscribe(() => trigger());
}

const lastPullKey = (name: string) => `epp.sync.${name}.lastPullAt`;

/**
 * Indique si un pull doit être lancé : évite que N onglets pullent chacun toutes les minutes.
 */
export function shouldPull(name: string, minIntervalMs = PULL_MIN_INTERVAL_MS): boolean {
    try {
        const lastPullAt = Number(localStorage.getItem(lastPullKey(name)) ?? 0);
        return Date.now() - lastPullAt >= minIntervalMs;
    } catch {
        return true;
    }
}

export function markPulled(name: string): void {
    try {
        localStorage.setItem(lastPullKey(name), String(Date.now()));
    } catch {
        // stockage indisponible : on pullera simplement plus souvent
    }
}

export function clearPullMarks(): void {
    try {
        Object.keys(localStorage)
            .filter(key => key.startsWith('epp.sync.'))
            .forEach(key => localStorage.removeItem(key));
    } catch {
        // rien à nettoyer
    }
}

export function logUpsertErrors(collectionName: string, result: { error?: any[] }): void {
    if (result?.error?.length) {
        console.warn(`[MultiTabSync] ${result.error.length} document(s) not upserted into [${collectionName}]:`, result.error);
    }
}

/**
 * fetch avec le token Okta courant ; lève une erreur si le token manque ou si la réponse n'est pas OK.
 */
export async function bearerFetch(url: string, oktaAuth: OktaAuth, init: RequestInit = {}): Promise<Response> {
    const authToken = oktaAuth.getAccessToken();
    if (!authToken) {
        throw new Error('Missing auth token');
    }
    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...(init.body ? {'Content-Type': 'application/json'} : {}),
        ...(init.headers as Record<string, string> ?? {})
    };
    const response = await fetch(url, {...init, headers});
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    return response;
}
